import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCampaignsQuery, useMetaCampaignsQuery } from '@/hooks/queries';
import { useLinkageEligibilityQuery } from '@/hooks/queries/useLinkageEligibilityQuery';
import { MetaCampaignCard } from '@/components/features/MetaCampaignCard';
import { useAppConfigQuery } from '@/hooks/queries/useOptimizationQuery';
import { CampaignCard } from '@/components/features/CampaignCard';
import { BudgetOptimizerSimple } from '@/components/features/BudgetOptimizer/BudgetOptimizerSimple';
import { AllocatableBudgetTooltipContent } from '@/components/features/BudgetOptimizer/AllocatableBudgetTooltipContent';
import { useTooltipHoverState } from '@/components/features/BudgetOptimizer/useTooltipHoverState';
import '@/styles/tooltip.css';
import { CacheIndicator } from '@/components/debug/CacheIndicator';
import { LinkAccountModal } from '@/components/LinkAccountModal';
import { shouldShowLinkAccountModal } from './linkAccountModalGate';
import { PollingManager } from '@/components/features/GoogleAdsLinker/PollingManager';
import { useCacheAge } from '@/hooks/useCacheAge';
import { debug } from '@/utils/debug';
import { useActiveAccountsCheck } from '@/hooks/useActiveAccountsCheck';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/authStore';
import { CreateCampaignWizard } from '@/components/features/CampaignCreation/CreateCampaignWizard';
import { CreateMetaCampaignWizard } from '@/components/features/MetaAdCreation/CreateMetaCampaignWizard';
import { OmniAccountPicker } from '@/components/features/OmniAccountPicker';
import { useCampaignSettingsGoogleAdsStore } from '@/store/campaignSettingsGoogleAdsStore';
import { useCampaignSettingsMetaAdsStore } from '@/store/campaignSettingsMetaAdsStore';
import { useAccountSettingsStore } from '@/store/accountSettingsStore';
import { usePendingChangesStore, type DiscardCallback } from '@/store/pendingChangesStore';
import type { Campaign } from '@/types/campaign';
import type { MetaCampaign } from '@/types/metaCampaign';
import { LoadingSpinner, SaveChangesBar } from '@/components/ui';
import { AISparkleIcon } from '@/components/ui/Icons';
import { useFeedback } from '@/hooks/useFeedback';
import { ROAS_CONFIG } from '@/config/constants';
import { applyGoogleOverlay, applyMetaOverlay } from '@/utils/savedConfigMerge';
import { FeedbackCard } from '@/components/features/FeedbackCard';
import { toast } from '@/utils';
import { useAccountChanges } from '@/hooks/useAccountChanges';
import { useOptimizedCampaigns } from '@/hooks/useOptimizedCampaigns';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';

// Filter options for ended campaigns
type EndedCampaignFilter = 'exclude' | 'last7days' | 'last30days' | 'all';

const FILTER_OPTIONS: { value: EndedCampaignFilter; label: string }[] = [
  { value: 'exclude', label: 'Exclude ended campaigns' },
  { value: 'last7days', label: 'Show ended within 7 days' },
  { value: 'last30days', label: 'Show ended within 30 days' },
  { value: 'all', label: 'Show all campaigns' },
];

// Tagged union for the unified campaign grid. The render loop branches
// on `platform` to pick `CampaignCard` vs `MetaCampaignCard` while a
// single comparator orders them together by budget.
type UnifiedCampaignEntry =
  | { platform: 'google_ads'; campaign: Campaign }
  | { platform: 'meta_ads'; campaign: MetaCampaign };

// Daily-budget value for the cross-platform sort. Google's `budget` IS
// the daily amount; Meta exposes `daily_budget` separately (with a
// parallel `lifetime_budget` for campaigns that don't run on a daily
// pace) so we treat a null/0 `daily_budget` as "no daily budget" and
// let the comparator group those after the daily-budget rows.
function dailyBudgetOf(entry: UnifiedCampaignEntry): number {
  if (entry.platform === 'google_ads') return entry.campaign.budget || 0;
  return entry.campaign.daily_budget ?? 0;
}

// Tie-breaker for entries without a daily budget: order by lifetime
// budget desc within the lifetime-budget group. A Google row with a
// nonzero `budget` lives in the daily-budget group and never reaches
// this tie-breaker; a Google row with `budget == 0` (uncommon — e.g.
// a freshly-created campaign that hasn't had a budget set) falls into
// the lifetime group and is treated as zero, sorting last.
function lifetimeBudgetOf(entry: UnifiedCampaignEntry): number {
  if (entry.platform === 'google_ads') return entry.campaign.budget || 0;
  return entry.campaign.lifetime_budget ?? 0;
}

export function Account() {
  const queryClient = useQueryClient();
  const { user, linkedAccountsReady } = useAuth();

  // Check if user has active accounts BEFORE loading campaigns
  // Never verify via API on this page - the campaigns endpoint already does account verification
  // This prevents slow page loads on reload
  const { isCheckingAccounts } = useActiveAccountsCheck({
    componentName: 'Account',
    verifyViaAPI: false
  });

  // Google-only `hasLinkedGoogleAccounts` (from /google-ads/accounts-summary via
  // authStore) is no longer used directly — the launcher + empty-state +
  // first-load link-modal effect all gate on the cross-platform
  // `hasAnyLinkedAccount` below. If a downstream consumer ever needs the
  // Google-specific signal back, derive it from `linkedAccountDetails` or
  // re-introduce the flag here.

  // Load both: metrics from campaigns API + saved config (which preserves all states)
  const {
    data: campaignsWithMetrics,
    isLoading: isLoadingMetrics,
    error: metricsError,
    dataUpdatedAt,
    refetch
  } = useCampaignsQuery();
  // Meta campaigns share this grid (A3 §9.1 — unified-grid override of
  // the original platform-tabs design). The hook is gated by
  // `hasLinkedMetaAccounts` so members without a Meta credential row
  // get enabled:false and no request is fired. Backend gates the
  // endpoint too.
  const {
    data: metaCampaigns,
    isLoading: isLoadingMetaCampaigns,
    error: metaCampaignsError,
  } = useMetaCampaignsQuery();
  // Use saved configuration to preserve flouri_optimize_bids, flouri_optimize_budget, and all other saved states
  const { data: savedConfig, isLoading: isLoadingConfig } = useAppConfigQuery();
  // Fetched here (in addition to LinkAccountModal, which uses it for
  // per-platform button visibility) so the link-modal effect below can
  // gate on the backend-authoritative linkage signal. See
  // `backendHasAnyLinkedPlatform` below for the semantics.
  const {
    data: eligibility,
    isLoading: isLoadingEligibility,
    isError: isEligibilityError,
  } = useLinkageEligibilityQuery();
  const [devMode, setDevMode] = useState(debug.isDev());
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showGoogleAdsLinker, setShowGoogleAdsLinker] = useState(false);
  const [showCreateCampaignWizard, setShowCreateCampaignWizard] = useState(false);
  const [showCreateMetaCampaignWizard, setShowCreateMetaCampaignWizard] = useState(false);
  // Omni picker surfaces only when 2+ linked accounts exist across any mix of
  // platforms. With 0 the button is upstream-gated; with 1 the launcher opens
  // that platform's wizard directly with the account pre-selected.
  const [showOmniAccountPicker, setShowOmniAccountPicker] = useState(false);
  // The account ID the user picked (or the auto-pick when 1 account). Passed
  // to the wizard via `preSelectedAccountId` so its Step 1 is bypassed.
  // Cleared when the wizard closes so a subsequent open re-runs the launcher.
  const [preSelectedGoogleAccountId, setPreSelectedGoogleAccountId] = useState<string | null>(null);
  const [preSelectedMetaAccountId, setPreSelectedMetaAccountId] = useState<string | null>(null);
  const { linkedMetaAccountDetails } = useAuthStore();
  const { linkedAccountDetails } = useAuth();

  // Omni account-aware launcher for `+ Create New Campaign`. Replaces the
  // earlier two-click flow ("which platform?" → then per-wizard "select
  // account") with one click that lists every linked ad account. The picked
  // account's platform determines which wizard launches; the wizard skips its
  // own Step 1 because `preSelectedAccountId` is set.
  const totalLinkedAccounts =
    linkedAccountDetails.length + linkedMetaAccountDetails.length;
  // `hasActiveAccounts` derives from /google-ads/accounts-summary only (see
  // authStore.ts), so gating the launcher on it hides the button from
  // Meta-only allowlisted users — exactly the user class the new
  // CreateMetaCampaignWizard was built for. Use the omni count instead.
  // Stays gated on `linkedAccountsReady` so we don't flash the button
  // during the initial linkage probe.
  const hasAnyLinkedAccount = linkedAccountsReady && totalLinkedAccounts > 0;

  // Backend-authoritative signal used ONLY to gate the link-account
  // popup effect below — NOT `hasAnyLinkedAccount`, whose consumers
  // (launcher + empty-state Create button) need actual account IDs from
  // the store to pre-fill wizards. Reads the DB-level
  // `has_linked_google` / `has_linked_meta` fields exposed by
  // /billing/linkage-eligibility (backed by `has_linked_platform`,
  // which ignores `is_active`). Suppresses the popup for members whose
  // /meta/accounts-summary returned empty (inactive credential row /
  // transient failure) even though the backend still counts them as
  // linked. Do NOT derive this by inverting `can_link_*` — those
  // express permission, not linkage, and Pro members are permitted to
  // link regardless of existing linkages so the inversion silently
  // breaks the moment Pro ships.
  const backendHasAnyLinkedPlatform = eligibility
    ? eligibility.has_linked_google || eligibility.has_linked_meta
    : false;

  const handleCreateCampaignClick = () => {
    if (totalLinkedAccounts === 0) {
      // Button is upstream-gated on hasActiveAccounts; this branch is a
      // defensive no-op.
      return;
    }
    if (totalLinkedAccounts === 1) {
      // Skip the picker entirely — open the right wizard pre-selected.
      if (linkedMetaAccountDetails.length === 1) {
        setPreSelectedMetaAccountId(linkedMetaAccountDetails[0].id);
        setShowCreateMetaCampaignWizard(true);
      } else {
        setPreSelectedGoogleAccountId(linkedAccountDetails[0].id);
        setShowCreateCampaignWizard(true);
      }
      return;
    }
    // 2+ accounts — let the user pick.
    setShowOmniAccountPicker(true);
  };
  const [endedCampaignFilter, setEndedCampaignFilter] = useState<EndedCampaignFilter>('exclude');
  // Optimistic status updates - tracks campaign status changes before server confirms
  const [optimisticStatuses, setOptimisticStatuses] = useState<Map<string, Campaign['status']>>(new Map());
  // Meta-side optimistic status updates — parallel mechanism to
  // `optimisticStatuses` (Google). Kept as a separate Map because
  // Meta and Google IDs share a string namespace (both bare digits)
  // and the two could collide on a same-id-across-platforms pool.
  // Reset alongside the Google one after refetch + on discard.
  const [optimisticMetaStatuses, setOptimisticMetaStatuses] = useState<Map<string, string>>(new Map());
  // Optimistic deletions - tracks campaigns that have been deleted but not yet confirmed by server
  const [optimisticDeletions, setOptimisticDeletions] = useState<Set<string>>(new Set());
  // Collapse state for the Budget Optimization Instructions - expanded by default on first visit in session
  const [showMobileRoasWarning, setShowMobileRoasWarning] = useState(false);
  const [revenuePerClickInput, setRevenuePerClickInput] = useState<string>('');
  const [isBudgetInstructionsHidden, setIsBudgetInstructionsHidden] = useState(() => {
    try {
      const stored = sessionStorage.getItem('budgetInstructionsHidden');
      // If never set (first visit), default to expanded (false); otherwise use stored value
      return stored !== null ? stored === 'true' : false;
    } catch {
      // Storage unavailable (e.g., private browsing, disabled)
      return false;
    }
  });

  // Info popover anchored to the "Automated Budget Optimization" banner.
  // Mirrors the popover on the Allocatable Budget card below — both
  // surfaces explain the same algorithm. Placing it on the intro banner
  // (which is always visible) means new users can reach the explanation
  // before they've selected any campaigns; the Allocatable Budget card
  // is gated on `hasOptimizedCampaigns` and so isn't available pre-select.
  const {
    isOpen: showAutoBudgetTooltip,
    open: openAutoBudgetTooltip,
    scheduleClose: scheduleCloseAutoBudgetTooltip,
  } = useTooltipHoverState();

  // Persist collapse state to sessionStorage
  const handleBudgetInstructionsToggle = () => {
    const newValue = !isBudgetInstructionsHidden;
    setIsBudgetInstructionsHidden(newValue);
    try {
      sessionStorage.setItem('budgetInstructionsHidden', String(newValue));
    } catch {
      // Storage unavailable - state still updates in memory
    }
  };

  // Check if any campaigns are selected for budget optimization - use selectors to avoid full store subscription
  const getOptimizedCampaigns = useCampaignSettingsGoogleAdsStore((state) => state.getOptimizedCampaigns);
  const setUseConversionBasedRoas = useAccountSettingsStore((state) => state.setUseConversionBasedRoas);
  const setRevenuePerClick = useAccountSettingsStore((state) => state.setRevenuePerClick);
  const hasOptimizedCampaigns = getOptimizedCampaigns().length > 0;

  // ROAS calculation settings from store - use selectors for reactivity on discard
  const useConversionBasedRoas = useAccountSettingsStore((state) => state.useConversionBasedRoas);
  const revenuePerClick = useAccountSettingsStore((state) => state.revenuePerClick);
  const originalSettings = useAccountSettingsStore((state) => state.originalSettings);

  // Check if revenue per click has changed from original (for reset button)
  // Only show reset if originalSettings exists (config was loaded) and value differs
  // Handle null explicitly: null is a distinct value meaning "unset", not the same as the default
  const originalRevenuePerClick = originalSettings?.revenuePerClick;
  const hasRevenuePerClickChange = originalSettings !== null &&
    revenuePerClick !== originalRevenuePerClick;

  // Sync local input state with store value (for controlled input that allows intermediate typing)
  useEffect(() => {
    setRevenuePerClickInput(String(revenuePerClick ?? ROAS_CONFIG.DEFAULT_REVENUE_PER_CLICK));
  }, [revenuePerClick]);

  // Get discard callback registration functions and clear function
  const registerDiscardCallback = usePendingChangesStore((state) => state.registerDiscardCallback);
  const unregisterDiscardCallback = usePendingChangesStore((state) => state.unregisterDiscardCallback);

  // Get discardChanges from all campaign-settings stores to clear on mount
  const discardGoogleOnMount = useCampaignSettingsGoogleAdsStore((state) => state.discardChanges);
  const discardMetaOnMount = useCampaignSettingsMetaAdsStore((state) => state.discardChanges);
  const discardAccountOnMount = useAccountSettingsStore((state) => state.discardChanges);

  // Calculate cache age for dev mode indicator
  const cacheAge = useCacheAge({
    dataUpdatedAt,
    enabled: import.meta.env.DEV && !!campaignsWithMetrics
  });

  // Feedback card state
  const feedback = useFeedback({ pageContext: 'account' });

  // Combined changes from pendingChangesStore + campaignSettingsGoogleAdsStore
  const accountChanges = useAccountChanges();

  // Merge campaigns: metrics from API + saved config (preserves ALL saved states)
  // Sort by Daily Budget in descending order. Per-platform overlay logic
  // lives in src/utils/savedConfigMerge.ts so adding a third platform is a
  // new overlay function + an applyXxxOverlay call here.
  const campaigns: Campaign[] = useMemo(() => {
    if (!campaignsWithMetrics) return [];

    const mergedCampaigns = campaignsWithMetrics.map((campaign) =>
      applyGoogleOverlay(campaign, savedConfig),
    );

    // Apply optimistic status updates
    const campaignsWithOptimisticStatus = mergedCampaigns.map(campaign => {
      const optimisticStatus = optimisticStatuses.get(campaign.campaign_id);
      if (optimisticStatus) {
        return { ...campaign, status: optimisticStatus };
      }
      return campaign;
    });

    // Filter out optimistically deleted campaigns
    const campaignsWithoutDeleted = campaignsWithOptimisticStatus.filter(
      campaign => !optimisticDeletions.has(campaign.campaign_id)
    );

    // Sort by Daily Budget in descending order
    return campaignsWithoutDeleted.sort((a, b) => b.budget - a.budget);
  }, [campaignsWithMetrics, savedConfig, optimisticStatuses, optimisticDeletions]);

  // Filter campaigns based on ended campaign filter
  const filteredCampaigns = useMemo(() => {
    if (endedCampaignFilter === 'all') return campaigns;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return campaigns.filter(campaign => {
      // If no end_date, campaign is ongoing - always show
      if (!campaign.end_date) return true;

      // Parse end_date as local date
      const [year, month, day] = campaign.end_date.split('-').map(Number);
      const endDate = new Date(year, month - 1, day);

      // If end_date is in the future, campaign hasn't ended - always show
      if (endDate >= today) return true;

      // Campaign has ended - apply filter
      if (endedCampaignFilter === 'exclude') return false;

      // Calculate days since campaign ended
      const daysSinceEnded = Math.floor((today.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24));

      if (endedCampaignFilter === 'last7days') return daysSinceEnded <= 7;
      if (endedCampaignFilter === 'last30days') return daysSinceEnded <= 30;

      return true;
    });
  }, [campaigns, endedCampaignFilter]);

  // Unified campaign grid feed — Google + Meta merged into one budget-
  // sorted list. Sort policy:
  //   1. Campaigns with a daily budget (>0) come first, ordered by
  //      daily budget desc.
  //   2. Campaigns without a daily budget (Meta lifetime-budget or
  //      ABO/ad-set-level) come last, ordered by lifetime budget desc.
  // The existing Google-only sort by `budget` inside `filteredCampaigns`
  // is preserved as a no-op input order — the cross-platform sort below
  // overrides it. Meta entries filter optimistic-archive + ARCHIVED
  // status here so the render loop stays a single map.
  const sortedCampaignEntries = useMemo<UnifiedCampaignEntry[]>(() => {
    const googleEntries: UnifiedCampaignEntry[] = filteredCampaigns.map(
      (campaign) => ({ platform: 'google_ads', campaign }),
    );
    // Meta terminal-status filter — matches the backend filter in
    // `account_configuration.py` (`_META_TERMINAL_STATUSES`) and the
    // tree filter in `MetaCampaignElementsList.tsx`
    // (`META_HIDDEN_STATUSES`). Case-insensitive so a lowercase /
    // mixed-case value from a future API change can't slip through.
    const META_TERMINAL_STATUSES = new Set(['ARCHIVED', 'DELETED']);
    const metaEntries: UnifiedCampaignEntry[] = (metaCampaigns ?? [])
      // Overlay saved-config state onto the live Meta API entry — same
      // contract as Google's applyGoogleOverlay above. Without this, a
      // saved Meta rename / flouri toggle / saved objective wouldn't
      // surface until the backend's regenerate_config_on_demand caught
      // up. See src/utils/savedConfigMerge.ts for the per-platform
      // overlay logic + the platform_source-aware lookup.
      .map((campaign) => applyMetaOverlay(campaign, savedConfig))
      // Apply optimistic status overlay — mirrors Google's
      // `optimisticStatus` overlay on `campaigns`. Sequenced AFTER
      // applyMetaOverlay so a queued pause doesn't get clobbered by
      // a saved-config refresh that hasn't seen the optimistic value
      // yet. Cleared after refetch (see refetchCampaigns) and on
      // discard (see Account discard callback).
      .map((campaign) => {
        const optimisticStatus = optimisticMetaStatuses.get(campaign.campaign_id);
        return optimisticStatus
          ? { ...campaign, campaign_status: optimisticStatus }
          : campaign;
      })
      .filter(
        (mc) =>
          !optimisticDeletions.has(mc.campaign_id) &&
          !META_TERMINAL_STATUSES.has((mc.campaign_status ?? '').toUpperCase()),
      )
      .map((campaign) => ({ platform: 'meta_ads', campaign }));
    const all = [...googleEntries, ...metaEntries];
    all.sort((a, b) => {
      const aDaily = dailyBudgetOf(a);
      const bDaily = dailyBudgetOf(b);
      const aHasDaily = aDaily > 0;
      const bHasDaily = bDaily > 0;
      if (aHasDaily !== bHasDaily) return aHasDaily ? -1 : 1;
      if (aHasDaily) return bDaily - aDaily;
      return lifetimeBudgetOf(b) - lifetimeBudgetOf(a);
    });
    return all;
  }, [filteredCampaigns, metaCampaigns, optimisticDeletions, optimisticMetaStatuses, savedConfig]);

  // Per-platform conversion-tracking signal. Each platform's flag is
  // true if ANY of its campaigns has reported conversion value > 0.
  const googleHasConversionsValue = useMemo(() =>
    campaigns.some(c => c.conversions_value != null && c.conversions_value > 0),
    [campaigns]
  );
  const metaHasConversionsValue = useMemo(() =>
    (metaCampaigns ?? []).some(c => c.metrics?.conversions_value != null && c.metrics.conversions_value > 0),
    [metaCampaigns]
  );

  // "None left behind" gating: Conversion Value is only allowed when
  // EVERY platform that has at least one Flouri-optimized campaign has
  // confirmed conversion data. A platform with no Flouri-optimized
  // campaigns doesn't gate the toggle (its cards still render via the
  // same flag, which is fine because conversion-based ROAS without
  // conversion data falls through to click-based inside
  // `computeCampaignRoas`).
  const googleHasFlouriOptimized = useMemo(() =>
    campaigns.some(c => c.flouri_optimize_budget === true),
    [campaigns]
  );

  // Cross-platform pool total. Computed once here and passed down to
  // every card via `poolTotalCurrentBudget`. Each card uses it for
  // pause/enable allocatable-budget math (mirror of Google's
  // `getTotalCurrentBudget()` lookup, but cross-platform-aware).
  // Lifted from per-card calls because N cards × `useOptimizedCampaigns()`
  // ran the full cross-store scan N times per render. Account page is
  // already the natural owner of cross-store state.
  const { totalCurrentBudget: poolTotalCurrentBudget } = useOptimizedCampaigns();
  const metaHasFlouriOptimized = useMemo(() =>
    (metaCampaigns ?? []).some(c => c.flouri_optimize_budget === true),
    [metaCampaigns]
  );
  const allFlouriOptimizedPlatformsHaveConversions = (
    (!googleHasFlouriOptimized || googleHasConversionsValue) &&
    (!metaHasFlouriOptimized || metaHasConversionsValue)
  );

  // hasAnyConversionsValue: kept for the legacy warning-text branches
  // below — true if at least one campaign on either platform has
  // reported value. This signals "tracking observed somewhere"
  // independently of the per-platform gating above.
  const hasAnyConversionsValue = googleHasConversionsValue || metaHasConversionsValue;

  // Effective method:
  //   * user prefers conversion-based, AND
  //   * at least one platform has reported conversion data anywhere
  //     (covers the "no Flouri-optimized campaigns but also no data"
  //     edge case where the gate below would be vacuously true), AND
  //   * the "none left behind" gate passes (every platform with at
  //     least one Flouri-optimized campaign has confirmed conversion
  //     data of its own).
  // Otherwise — toggle off, no data anywhere, or a Flouri-optimized
  // platform is missing data — every card falls back to click-based.
  const effectiveUseConversionBasedRoas = (
    useConversionBasedRoas &&
    hasAnyConversionsValue &&
    allFlouriOptimizedPlatformsHaveConversions
  );

  // Show revenue per click input when using click-based (either by preference or fallback)
  const showRevenuePerClickInput = !effectiveUseConversionBasedRoas;

  // Explanatory text when the Conversion Value radio is selected but
  // the effective method is click-based. Two distinct reasons, each
  // gets its own copy — telling a user "no conversion data observed
  // yet" when their Google account is reporting purchases just
  // because Meta isn't would be confusing.
  const conversionFallbackReason: string | null = (
    !useConversionBasedRoas || effectiveUseConversionBasedRoas
      ? null
      : !hasAnyConversionsValue
        ? 'No conversion data observed yet. Will use click-based ROAS until tracking is confirmed.'
        : 'Conversion data not yet observed on every Flouri-optimized platform. Will use click-based ROAS until every platform reports conversions.'
  );

  // Include `isLoadingEligibility` so the link-modal effect waits for
  // the backend-authoritative linkage signal before deciding to fire —
  // without this, a first paint after login could see
  // `hasAnyLinkedAccount=false` (auth-store hasn't resolved yet OR
  // Meta preload came back empty) with eligibility still pending, and
  // open the popup before we know the backend actually has Meta linked.
  const isLoading = isCheckingAccounts || isLoadingMetrics || isLoadingConfig || isLoadingMetaCampaigns || isLoadingEligibility || !linkedAccountsReady;
  // Surface either platform's query error to the page-level error UI —
  // a silent Meta failure would leave the unified grid looking
  // "successful" while dropping Meta entries. Google takes precedence
  // when both fire because the Google grid is older / higher-cardinality.
  const error = metricsError ?? metaCampaignsError;

  const refetchCampaigns = React.useCallback(async (options?: { includeCampaignsQuery?: boolean }) => {
    debug.log('Account: Invalidating queries for campaign updates', { includeCampaignsQuery: options?.includeCampaignsQuery });
    // Always invalidate app-config query - status changes and saved states are stored there
    await queryClient.invalidateQueries({ queryKey: ['app-config'] });
    // Always invalidate the Meta campaigns query so a Meta status /
    // rename change shipped via the SaveChangesBar pulls fresh status
    // from the regenerated saved config on the next read. Without this
    // the MetaCampaignCard kebab stays out of sync (e.g. shows "Pause
    // campaign" right after the user already paused) until a hard
    // reload.
    await queryClient.invalidateQueries({ queryKey: ['meta-campaigns'] });
    // Optionally invalidate 'campaigns' query when new campaigns are created/deleted
    // This triggers a Google Ads API fetch which can be slow, so only do it when needed
    if (options?.includeCampaignsQuery) {
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    }
    // Clear optimistic states after refetch — server data is authoritative
    // for everything we just refetched. PRESERVE optimistic state for any
    // campaign whose status change is still in the pending queue: those
    // are post-snapshot additions (queued by the user during an in-flight
    // save) and the queue + optimistic state must stay in lockstep. A
    // blanket wipe here makes the bar say "1 unsaved change" while the
    // campaign card visually reverts to the server state — the UI lies
    // and the user reasonably reads it as "the save broke optimistic
    // updating." The race-fix in saveAllChanges (per-item cleanup +
    // dispatched-keys contract) preserves the queue; this filter keeps
    // the overlay consistent with it.
    const pendingStatusChanges = usePendingChangesStore.getState().changes.statusChanges;
    const pendingGoogleCampaignIds = new Set<string>();
    const pendingMetaCampaignIds = new Set<string>();
    const pendingRemovalIds = new Set<string>();
    for (const change of pendingStatusChanges.values()) {
      if (change.entityType !== 'campaign') continue;
      const platform = change.platform ?? 'google_ads';
      if (platform === 'meta_ads') {
        pendingMetaCampaignIds.add(change.campaignId);
      } else {
        pendingGoogleCampaignIds.add(change.campaignId);
        if (change.action === 'remove') {
          pendingRemovalIds.add(change.campaignId);
        }
      }
    }
    setOptimisticStatuses(prev => {
      if (pendingGoogleCampaignIds.size === 0) return new Map();
      const next = new Map<string, Campaign['status']>();
      for (const [id, status] of prev) {
        if (pendingGoogleCampaignIds.has(id)) next.set(id, status);
      }
      return next;
    });
    setOptimisticMetaStatuses(prev => {
      if (pendingMetaCampaignIds.size === 0) return new Map();
      const next = new Map<string, string>();
      for (const [id, status] of prev) {
        if (pendingMetaCampaignIds.has(id)) next.set(id, status);
      }
      return next;
    });
    setOptimisticDeletions(prev => {
      if (pendingRemovalIds.size === 0) return new Set();
      const next = new Set<string>();
      for (const id of prev) {
        if (pendingRemovalIds.has(id)) next.add(id);
      }
      return next;
    });
  }, [queryClient]);

  // Handler for optimistic status updates from CampaignCard
  const handleCampaignStatusChange = React.useCallback((campaignId: string, newStatus: string) => {
    debug.log('Account: Optimistic status update', { campaignId, newStatus });
    setOptimisticStatuses(prev => {
      const next = new Map(prev);
      next.set(campaignId, newStatus as Campaign['status']);
      return next;
    });
  }, []);

  // Handler for optimistic status updates from MetaCampaignCard.
  // Parallel to `handleCampaignStatusChange` (Google) — overlaid onto
  // `metaCampaigns` in the `sortedCampaignEntries` useMemo so the
  // card receives the new status via its `campaign` prop. Cleared on
  // refetch (server data is authoritative) + on discard.
  const handleMetaCampaignStatusChange = React.useCallback(
    (campaignId: string, newStatus: string) => {
      debug.log('Account: Optimistic Meta status update', { campaignId, newStatus });
      setOptimisticMetaStatuses(prev => {
        const next = new Map(prev);
        next.set(campaignId, newStatus);
        return next;
      });
    },
    [],
  );

  // Handler for optimistic deletion from CampaignCard
  const handleCampaignOptimisticDelete = React.useCallback((campaignId: string) => {
    debug.log('Account: Optimistic deletion', { campaignId });
    setOptimisticDeletions(prev => {
      const next = new Set(prev);
      next.add(campaignId);
      return next;
    });
  }, []);

  // Function to refresh linked accounts (used after linking a new account)
  const refreshLinkedAccounts = React.useCallback(async () => {
    if (!user?.memberUuid) {
      return;
    }

    debug.log('Account: Refreshing linked accounts after account link');

    // Trigger a fresh preload in the auth store
    const { preloadLinkedAccounts } = useAuthStore.getState();
    await preloadLinkedAccounts(user.memberUuid);

    // Also refetch campaigns
    await refetchCampaigns();

    debug.log('Account: Linked accounts refreshed');
  }, [user?.memberUuid, refetchCampaigns]);

  React.useEffect(() => {
    debug.log('Account: Component mounted');
  }, []);

  // Reset Google Ads campaign-settings local store on mount. We do NOT
  // call `clearAllChanges()` on `pendingChangesStore` here — that would
  // wipe Meta status changes queued from the Campaign detail page's
  // `MetaCampaignElementsList` tree when the user navigates back to
  // Account mid-batch. The pendingChangesStore queue is legitimately
  // cleared on Save success, user-clicked Discard, or logout.
  //
  // Ref-guarded so it fires exactly once per mount. The Zustand-action
  // deps are stable function refs in normal operation, but if anything
  // makes them unstable (HMR module replacement, store recreation,
  // etc.) this effect would re-fire and cascade into a re-render loop
  // with the per-store discard handlers — observed on a Phase 1c
  // smoketest after a Meta budget save. The guard makes the intended
  // "mount-only" semantics enforceable.
  const hasResetOnMountRef = useRef(false);
  useEffect(() => {
    if (hasResetOnMountRef.current) return;
    hasResetOnMountRef.current = true;
    discardGoogleOnMount();
    discardMetaOnMount();
    discardAccountOnMount();
  }, [discardGoogleOnMount, discardMetaOnMount, discardAccountOnMount]);

  // Hydrate every slice provider from the saved app_config exactly once
  // when the React Query fetch resolves. Without this, accountSettingsStore
  // never gets an `originalSettings` baseline → setters can't flag dirty
  // state → SaveChangesBar never shows account-level changes for brand-new
  // users editing ROAS / total budget / etc. Reuses the savedConfig that
  // useAppConfigQuery already fetched (no extra round-trip).
  //
  // `savedConfig` is AppConfiguration | null (the query returns null on a
  // 404 / new user, per useOptimizationQuery). Hydrating with `undefined`
  // in that case is correct: account store seeds from DEFAULT_ACCOUNT_SETTINGS;
  // Google / Meta stores early-return on missing campaign_list.
  //
  // One-shot via ref so a subsequent cache invalidation (e.g. after Save)
  // doesn't clobber in-flight user edits — matches the existing
  // BudgetOptimizerSimple `hasLoadedInitialConfig` pattern.
  const hasHydratedFromSavedConfig = useRef(false);
  useEffect(() => {
    if (hasHydratedFromSavedConfig.current) return;
    if (isLoadingConfig) return;
    hasHydratedFromSavedConfig.current = true;
    const config = savedConfig?.configuration;
    useCampaignSettingsGoogleAdsStore.getState().hydrateFromConfig(config);
    useCampaignSettingsMetaAdsStore.getState().hydrateFromConfig(config);
    useAccountSettingsStore.getState().hydrateFromConfig(config);
  }, [savedConfig, isLoadingConfig]);

  // Phase 1b — seed the Meta settings store from live `useMetaCampaignsQuery`
  // data on every cache update. Without this, `isCbo` + `isDailyBudget`
  // never get populated (they aren't in the saved-config blob that
  // `hydrateFromConfig` reads), `useOptimizedCampaigns` filters every
  // Meta candidate out as ineligible, and the cross-platform optimizer
  // never sees a Meta campaign even when the user toggles flouri on.
  // `currentBudget` is also seeded here from the live `daily_budget`
  // so the BudgetBar in MetaCampaignCard has a meaningful baseline.
  //
  // `initializeCampaignSettings` has a round-3 preservation policy:
  // user-staged manualName / manualBudget / toggle survive a refetch
  // when the campaign is in the corresponding dirty Set, so subsequent
  // React Query cache invalidations (after a Save) don't clobber
  // in-flight edits. Google has an equivalent call inside
  // BudgetOptimizerSimple (line 152); the Account page is the
  // analogous seed point for Meta because BudgetOptimizerSimple's
  // `campaigns` prop is Google-only by design.
  useEffect(() => {
    // Only skip the call when the query is still pending (undefined);
    // an empty array MUST flow through so the store can drop stale
    // entries (e.g. after a Meta account unlink). The store's
    // `initializeCampaignSettings` now rebuilds the Map from the
    // incoming payload — calling it with [] is the explicit clear.
    if (!metaCampaigns) return;
    useCampaignSettingsMetaAdsStore.getState().initializeCampaignSettings(metaCampaigns);
  }, [metaCampaigns]);

  // Show link account modal only for users who don't have any linked accounts
  // If they have linked accounts but no campaigns, show different UI (create campaign)
  useEffect(() => {
    if (
      shouldShowLinkAccountModal({
        isLoading,
        hasGoogleCampaigns: campaigns.length > 0,
        hasAnyLinkedAccount,
        backendHasAnyLinkedPlatform,
        isEligibilityError,
      })
    ) {
      debug.log('Account: No campaigns and no linked accounts, showing link account modal');
      setShowLinkModal(true);
    }
  }, [
    isLoading,
    campaigns.length,
    hasAnyLinkedAccount,
    backendHasAnyLinkedPlatform,
    isEligibilityError,
  ]);

  // Listen for debug mode changes
  useEffect(() => {
    const updateMode = () => {
      const currentMode = debug.isDev();
      setDevMode(currentMode);
      console.log('Account: Mode updated to', currentMode ? 'DEVELOPMENT' : 'PRODUCTION');
    };
    const unsubscribe = debug.onModeChange(updateMode);
    updateMode();
    return unsubscribe;
  }, []);

  // Register discard callback to clear optimistic state when user clicks "Discard"
  useEffect(() => {
    const discardCallbackKey = 'account-optimistic-state';

    const restoreState: DiscardCallback = () => {
      debug.log('Account: Discard callback executing - clearing optimistic state');
      // Clear optimistic status updates (Google + Meta)
      setOptimisticStatuses(new Map());
      setOptimisticMetaStatuses(new Map());
      // Clear optimistic deletions
      setOptimisticDeletions(new Set());
    };

    registerDiscardCallback(discardCallbackKey, restoreState);

    return () => {
      unregisterDiscardCallback(discardCallbackKey);
    };
  }, [registerDiscardCallback, unregisterDiscardCallback]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <LoadingSpinner size="lg" variant="primary" className="mx-auto mb-4" label="Loading campaigns" />
          <p className="text-gray-600">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Campaigns</h2>
        <p className="text-gray-600">
          {error instanceof Error ? error.message : 'An unexpected error occurred'}
        </p>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <div>
      {/* Link Account Modal for new users */}
      <LinkAccountModal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        onLinkAccount={() => setShowGoogleAdsLinker(true)}
      />

      {/* Cache Age Indicator (Dev Mode Only) */}
      {campaignsWithMetrics && devMode && (
        <CacheIndicator
          cacheAge={cacheAge}
          onRefresh={() => refetch()}
          label="Campaign Data"
        />
      )}

      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Campaign Dashboard</h1>
        <p className="text-gray-600">Manage and optimize your ad campaigns</p>
      </div>

      {sortedCampaignEntries.length > 0 ? (
        <>
          {/* Top Cards Section with Feedback Card */}
          <div className="relative">
            {/* Feedback Card - Absolutely positioned to the right of centered cards, fixed at top */}
            {feedback.isVisible && feedback.currentQuestion && (
              <div className="hidden xl:block absolute top-0 right-4 w-56">
                <FeedbackCard
                  question={feedback.currentQuestion}
                  onSubmit={feedback.submitResponse}
                  isSubmitting={feedback.isSubmitting}
                  error={feedback.error}
                />
              </div>
            )}

            {/* Campaign Stats Summary — derived from the unified
                Google + Meta grid (`sortedCampaignEntries`), not from
                `campaigns` (Google-only). Otherwise Meta campaigns
                render in the grid below but don't get counted here. */}
            <div className="mb-8 flex justify-center">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-2xl w-full">
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Total Campaigns</p>
                    <p className="text-2xl font-bold text-gray-900">{sortedCampaignEntries.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Enabled / Active Campaigns</p>
                    <p className="text-2xl font-bold text-green-600">
                      {sortedCampaignEntries.filter((entry) => {
                        if (entry.platform === 'google_ads') {
                          const s = entry.campaign.status;
                          return s === 'ENABLED' || s?.includes('ENABLED');
                        }
                        return (entry.campaign.campaign_status ?? '').toUpperCase() === 'ACTIVE';
                      }).length}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Total Daily Budget</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${sortedCampaignEntries.reduce((sum, entry) => sum + dailyBudgetOf(entry), 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Budget Optimization Instructions. Outer wrapper is
                `relative` so the downward-pointing info popover can
                position absolutely relative to it (centered on the page
                via left:50% on this full-width flex container). */}
            <div className="mb-6 flex justify-center relative">
              {showAutoBudgetTooltip && (
                <div
                  id="auto-budget-optimization-tooltip"
                  role="tooltip"
                  className="tooltip-info-popover tooltip-info-popover-below"
                  onMouseEnter={openAutoBudgetTooltip}
                  onMouseLeave={scheduleCloseAutoBudgetTooltip}
                >
                  <AllocatableBudgetTooltipContent />
                </div>
              )}
              <div className="max-w-2xl w-full rounded-lg shadow-sm overflow-hidden">
                {/* Title Banner - dark blue */}
                <div className="bg-blue-700 px-5 py-3 flex items-center gap-2">
                  <AISparkleIcon className="w-8 h-8 flex-shrink-0 -mt-1 text-white" />
                  <h2 className="text-lg font-semibold text-white">Automated Budget Optimization</h2>
                  <button
                    type="button"
                    aria-label="About automated budget optimization"
                    // Only point at the tooltip id while it's actually
                    // in the DOM. The tooltip is conditionally rendered
                    // on `showAutoBudgetTooltip`; an `aria-describedby`
                    // referencing a non-existent id is invalid ARIA and
                    // can confuse screen readers (Copilot review on
                    // PR #206).
                    aria-describedby={showAutoBudgetTooltip ? "auto-budget-optimization-tooltip" : undefined}
                    onMouseEnter={openAutoBudgetTooltip}
                    onMouseLeave={scheduleCloseAutoBudgetTooltip}
                    onFocus={openAutoBudgetTooltip}
                    onBlur={scheduleCloseAutoBudgetTooltip}
                    className="ml-1 text-blue-200 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-full"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>

                {/* Content area - light blue */}
                <div className="bg-blue-50 border border-t-0 border-blue-200 rounded-b-lg">
                  {/* Main instruction with toggle arrow */}
                  <div className="px-5 pt-3 pb-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-blue-700 font-medium">
                        Select campaigns below to have Flouri optimize their budgets automatically
                      </p>
                      <button
                        onClick={handleBudgetInstructionsToggle}
                        className="ml-3 p-1 text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded transition-colors flex-shrink-0"
                        aria-label={isBudgetInstructionsHidden ? "Show instructions" : "Hide instructions"}
                        aria-expanded={!isBudgetInstructionsHidden}
                        aria-controls="budget-instructions"
                      >
                        <svg
                          className={`w-5 h-5 transition-transform duration-200 ${isBudgetInstructionsHidden ? '' : 'rotate-180'}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Collapsible instructions */}
                  <div
                    id="budget-instructions"
                    className={`overflow-hidden transition-all duration-200 ease-out ${
                      isBudgetInstructionsHidden ? 'max-h-0' : 'max-h-screen'
                    }`}
                  >
                    <div className="px-5 pb-4 border-t border-blue-200">
                      <div className="pt-3 space-y-2 text-sm text-blue-800">
                        <div className="flex items-start gap-2">
                          <span className="text-blue-600 mt-0.5 font-bold">•</span>
                          <span>
                            Press the{' '}
                            <span className="inline-flex items-center justify-center bg-white border border-blue-200 rounded-full w-[22px] h-[22px] align-middle mx-0.5 -mt-0.5">
                              <svg
                                className="w-3.5 h-3.5"
                                viewBox="0 0 637.48 637.65"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path fill="#b7bcc5" d="M452.13,0h-.31c-102.67,0-185.9,83.23-185.9,185.9v169.54c0,8.9,7.22,16.12,16.12,16.12h170.09c102.36,0,185.34-82.98,185.34-185.34v-.87C637.48,82.98,554.49,0,452.13,0ZM490.17,234.49c0,45.74-37.08,82.82-82.82,82.82h-71.99c-6.12,0-11.07-4.96-11.07-11.07v-71.74c0-45.88,37.19-83.06,83.06-83.06h0c45.74,0,82.82,37.08,82.82,82.82v.25Z" />
                                <path fill="#b7bcc5" d="M215.11,356.47v-92.47c0-59.4-48.15-107.55-107.55-107.55h0C48.15,156.45,0,204.6,0,264h0c0,59.4,48.15,107.55,107.55,107.55h92.47c8.33,0,15.08-6.75,15.08-15.08Z" />
                                <path fill="#b7bcc5" d="M265.92,438.11v91.98c0,59.4,48.15,107.55,107.55,107.55h0c59.4,0,107.55-48.15,107.55-107.55h0c0-59.4-48.15-107.55-107.55-107.55h-91.98c-8.6,0-15.57,6.97-15.57,15.57Z" />
                                <path fill="#b7bcc5" d="M199.53,422.54h-91.98c-59.4,0-107.55,48.15-107.55,107.55h0c0,59.4,48.15,107.55,107.55,107.55h0c59.4,0,107.55-48.15,107.55-107.55v-91.98c0-8.6-6.97-15.57-15.57-15.57Z" />
                              </svg>
                            </span>{' '}
                            on campaign cards to include them in optimization
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-blue-600 mt-0.5 font-bold">•</span>
                          <span>Only <strong>Enabled</strong> campaigns are included in optimization; <strong>Paused</strong> campaigns are excluded and their budgets will not change</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="text-blue-600 mt-0.5 font-bold">•</span>
                          <span>Your <strong>Allocatable Budget</strong> is distributed for maximum performance - adjust below as needed.</span>
                        </div>
                        <div className="pt-2 mt-2 border-t border-blue-200">
                          <p className="text-blue-800">
                            Press <span className="font-semibold">Save Changes</span> after updating your budget or optimization settings
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End of Top Cards Section */}

          {/* Campaign Filter and ROAS Settings */}
          <div className="mb-6 space-y-3">
            {/* Filter Row */}
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                <label htmlFor="ended-filter" className="text-sm font-medium text-gray-700">
                  Filter:
                </label>
                <select
                  id="ended-filter"
                  value={endedCampaignFilter}
                  onChange={(e) => setEndedCampaignFilter(e.target.value as EndedCampaignFilter)}
                  className="flex-1 md:flex-none md:min-w-[220px] px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {FILTER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-sm text-gray-500">
                {/* Count what's actually rendered in the unified grid
                    (Google + Meta), not just the Google slice. The
                    of-total adds back the Google rows the end-date
                    filter excluded so the user can see what the
                    filter dropped. Meta entries aren't affected by
                    `endedCampaignFilter` (it keys on
                    Google's `end_date`), so they contribute the same
                    count to both halves and the "of" only diverges
                    when the Google end-date filter is excluding rows. */}
                Showing {sortedCampaignEntries.length} of {sortedCampaignEntries.length + (campaigns.length - filteredCampaigns.length)} campaigns
              </div>
            </div>

            {/* ROAS Calculation Toggle */}
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-700 mb-1">ROAS Calculation:</span>

              {/* Line 1: Conversion Value + warning message */}
              <div className="flex items-center gap-2 flex-wrap ml-4">
                <label className="flex items-center gap-1.5 cursor-pointer min-w-[140px]">
                  <input
                    type="radio"
                    name="roas-calculation"
                    checked={useConversionBasedRoas}
                    onChange={() => setUseConversionBasedRoas(true)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Conversion Value</span>
                </label>

                {/* Info message - desktop: inline with Conversion Value */}
                {conversionFallbackReason && (
                  <div className="hidden md:flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs text-amber-700">
                      {conversionFallbackReason}
                    </span>
                  </div>
                )}

                {/* Info icon - mobile: clickable tooltip */}
                {conversionFallbackReason && (
                  <div className="relative md:hidden">
                    <button
                      type="button"
                      onClick={() => setShowMobileRoasWarning(!showMobileRoasWarning)}
                      className="p-0.5 text-amber-500 hover:text-amber-600"
                      aria-label="Show warning about conversion tracking"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {showMobileRoasWarning && (
                      <div className="absolute left-1/2 -translate-x-1/2 top-6 z-10 w-72 p-2 bg-amber-50 border border-amber-200 rounded-md shadow-lg">
                        <span className="text-xs text-amber-700">
                          {conversionFallbackReason}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Line 2: Click Value + revenue per click input */}
              <div className="flex flex-wrap items-center gap-2 md:flex-nowrap md:gap-4 ml-4">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="roas-calculation"
                    checked={!useConversionBasedRoas}
                    onChange={() => setUseConversionBasedRoas(false)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Click Value</span>
                </label>

                {/* Revenue per click input - shown when using click-based */}
                {showRevenuePerClickInput && (
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-500">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={revenuePerClickInput}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        // Allow digits, single decimal point, and empty string while typing
                        if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                          setRevenuePerClickInput(rawValue);
                        }
                      }}
                      onBlur={() => {
                        // On blur, validate and commit to store only if value actually changed
                        const currentStoreValue = revenuePerClick ?? ROAS_CONFIG.DEFAULT_REVENUE_PER_CLICK;

                        // Empty input means "use default" - set store to null
                        if (revenuePerClickInput.trim() === '') {
                          if (revenuePerClick !== null) {
                            setRevenuePerClick(null);
                          }
                          // Update display to show the default value
                          setRevenuePerClickInput(String(ROAS_CONFIG.DEFAULT_REVENUE_PER_CLICK));
                          return;
                        }

                        const parsedValue = parseFloat(revenuePerClickInput);

                        // Invalid input: reset display to current store value
                        if (isNaN(parsedValue) || parsedValue < 0.01) {
                          setRevenuePerClickInput(String(currentStoreValue));
                          return;
                        }

                        // Compare as numbers to handle formatting differences (e.g., "0.10" vs "0.1")
                        // Only commit if value actually changed
                        if (parsedValue !== currentStoreValue) {
                          setRevenuePerClick(parsedValue);
                        }
                        // Normalize the display (e.g., "0.10" -> "0.1")
                        setRevenuePerClickInput(String(parsedValue));
                      }}
                      className={`w-20 px-2 py-1 text-base sm:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        hasRevenuePerClickChange ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                      }`}
                      aria-label="Revenue per click"
                    />
                    <span className="text-sm text-gray-500">/click</span>

                    {/* Reset button - same style as BudgetBar reset */}
                    {hasRevenuePerClickChange && (
                      <button
                        type="button"
                        onClick={() => setRevenuePerClick(originalRevenuePerClick ?? null)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-700 bg-blue-100 hover:bg-blue-200 border border-blue-200 hover:border-blue-300 rounded shadow-sm hover:shadow transition-all whitespace-nowrap font-medium ml-1"
                        title="Reset to saved value"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                        reset
                      </button>
                    )}
                  </div>
                )}
              </div>

            </div>
          </div>

          {/* Unified campaign grid — Google and Meta cards share the same
              auto-fill grid and are interleaved by daily-budget desc via
              `sortedCampaignEntries`. Each card type renders
              identically-shaped tiles so the layout reads as one
              cohesive set. A3 §9.1 unified-grid (overrides PlatformTabs).

              `endedCampaignFilter` (Google's end_date pipeline) still
              applies only to Google entries via `filteredCampaigns`
              upstream of the sort; Meta's `stop_time` analog isn't
              wired into the filter yet. Acceptable until the filter
              UX adds a platform-aware predicate. Server-side, the
              saved-config regenerate only filters by linked-account
              `is_active` (not by `stop_time` / ended state), so a Meta
              campaign past its `stop_time` will still render here. */}
          <div className="grid gap-6 grid-cols-[repeat(auto-fill,minmax(360px,1fr))] mb-8">
            {sortedCampaignEntries.map((entry) =>
              entry.platform === 'google_ads' ? (
                <CampaignCard
                  key={`google-${entry.campaign.campaign_id}`}
                  campaign={entry.campaign}
                  revenuePerClick={revenuePerClick ?? ROAS_CONFIG.DEFAULT_REVENUE_PER_CLICK}
                  // Pass the gated value, not the raw toggle — if the
                  // none-left-behind gate fails (some Flouri-optimized
                  // platform missing conversion data), every card
                  // collapses to click-based.
                  useConversionBasedRoas={effectiveUseConversionBasedRoas}
                  // Per-platform: still drives this card's actual ROAS
                  // computation (`shouldUseConversionBased`), since a
                  // platform without conversions can't compute
                  // conversion-based ROAS regardless of intent. The
                  // disclaimer copy ("Click-based" vs "No conversion
                  // data") is driven by `userPickedClickBased` below,
                  // not this flag, so Google and Meta cards stay
                  // aligned when the user has actively chosen click-
                  // based via the toggle.
                  conversionTrackingAvailable={googleHasConversionsValue}
                  // Raw user preference, not the gated "effective"
                  // value: when the user pressed Click Value, both
                  // platforms should report "Click-based · $X/click"
                  // as the user's choice — regardless of which
                  // platforms happen to have conversion data.
                  userPickedClickBased={!useConversionBasedRoas}
                  poolTotalCurrentBudget={poolTotalCurrentBudget}
                  onOptimisticDelete={handleCampaignOptimisticDelete}
                  onDelete={async () => {
                    debug.log('Account: Campaign deleted, refreshing data');
                    // Include campaigns query invalidation to remove deleted campaign from the list
                    await refetchCampaigns({ includeCampaignsQuery: true });
                  }}
                  onStatusChange={handleCampaignStatusChange}
                />
              ) : (
                // Meta now mirrors Google's ROAS plumbing. Shared
                // `revenue_per_click` (level playing field) +
                // account-level "none left behind" gating drive both
                // platforms uniformly.
                <MetaCampaignCard
                  key={`meta-${entry.campaign.campaign_id}`}
                  campaign={entry.campaign}
                  revenuePerClick={revenuePerClick ?? ROAS_CONFIG.DEFAULT_REVENUE_PER_CLICK}
                  useConversionBasedRoas={effectiveUseConversionBasedRoas}
                  conversionTrackingAvailable={metaHasConversionsValue}
                  // Parity with the Google card above — raw user
                  // preference drives the disclaimer copy so a Meta
                  // card with no conversion data still reads "Click-
                  // based · $X/click" when the user actively pressed
                  // Click Value.
                  userPickedClickBased={!useConversionBasedRoas}
                  poolTotalCurrentBudget={poolTotalCurrentBudget}
                  onOptimisticDelete={handleCampaignOptimisticDelete}
                  onStatusChange={handleMetaCampaignStatusChange}
                />
              )
            )}
          </div>

          {/* Create New Campaign Button — visible whenever either platform
              is linked (Google-only, Meta-only, or both). */}
          {hasAnyLinkedAccount && (
            <div className="mb-8 flex justify-center">
              <button
                onClick={handleCreateCampaignClick}
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm hover:shadow-md font-medium"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Create New Campaign
              </button>
            </div>
          )}

          {/* Budget Optimizer Controls */}
          <div className="mb-8">
            <BudgetOptimizerSimple campaigns={campaigns} onCampaignsUpdate={refetchCampaigns} />
          </div>

          {/* Reserve space when no campaigns are optimized to prevent page shift when cards expand */}
          {/* Animates in/out in sync with the budget optimizer card */}
          <div
            className="grid transition-[grid-template-rows] duration-300 ease-out"
            style={{ gridTemplateRows: hasOptimizedCampaigns ? '0fr' : '1fr' }}
          >
            <div className="overflow-hidden">
              <div className="h-[350px]" />
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          {hasAnyLinkedAccount ? (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No Campaigns Found</h2>
              <p className="text-gray-600 mb-6">
                Your ad accounts are linked but have no campaigns. Create a new campaign to get started.
              </p>
              <button
                onClick={handleCreateCampaignClick}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Create New Campaign
              </button>
            </>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No Campaigns Found</h2>
              <p className="text-gray-600 mb-6">
                Link an ads account to start optimizing your campaigns.
              </p>
              <button
                onClick={() => setShowLinkModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-800 transition-colors"
              >
                Link Ads Account
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
              </button>
            </>
          )}
        </div>
      )}

      {devMode && (
        <div className="mt-8 p-4 bg-gray-100 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">🔧 Debug Info</h3>
          <pre className="text-xs bg-gray-800 text-white p-2 rounded overflow-auto">
            {JSON.stringify(
              campaigns.map(({ ...rest }) => rest),
              null,
              2
            )}
          </pre>
        </div>
      )}

      {/* Google Ads Linking Flow */}
      <PollingManager
        isOpen={showGoogleAdsLinker}
        onClose={() => {
          setShowGoogleAdsLinker(false);
        }}
        onAccountLinked={async () => {
          // Refresh account data immediately when account is linked
          await refreshLinkedAccounts();
        }}
      />

      {/* Create Campaign Wizard */}
      <CreateCampaignWizard
        isOpen={showCreateCampaignWizard}
        onClose={() => {
          setShowCreateCampaignWizard(false);
          // Clear the pre-selection so the next launcher click re-runs the
          // 0/1/2+ decision tree from scratch (without this, a subsequent
          // "1 linked account" scenario would silently reuse a stale id).
          setPreSelectedGoogleAccountId(null);
        }}
        onSuccess={async () => {
          // Refresh campaigns after successful creation and wait for it to complete
          // Include campaigns query invalidation to fetch the newly created campaign from Google Ads
          await refetchCampaigns({ includeCampaignsQuery: true });
        }}
        preSelectedAccountId={preSelectedGoogleAccountId}
      />

      {/* Create Meta Campaign Wizard (A4.3.1) */}
      <CreateMetaCampaignWizard
        isOpen={showCreateMetaCampaignWizard}
        onClose={() => {
          setShowCreateMetaCampaignWizard(false);
          setPreSelectedMetaAccountId(null);
        }}
        onSuccess={async () => {
          // The backend route DOES trigger an on-demand Cloud Run sync after
          // create (sync_ads_data_background, platform='meta_ads') AND
          // writes an optimistic app_configuration entry with the new
          // campaign in `meta_ads_data.campaigns_daily` shape — so the card
          // appears on the grid immediately, then gets reconciled by the
          // sync job ~30s later. We just invalidate so the next read picks
          // up the optimistic write + later sync results.
          await queryClient.invalidateQueries({ queryKey: ['meta-campaigns'] });
          await queryClient.invalidateQueries({ queryKey: ['app-config'] });
        }}
        preSelectedAccountId={preSelectedMetaAccountId}
      />

      {/* Omni account picker (only when 2+ linked accounts across any mix
          of platforms). Replaces the prior PlatformChooserDialog +
          per-wizard Step 1 (account picker) two-click flow with one. */}
      <OmniAccountPicker
        isOpen={showOmniAccountPicker}
        googleAccounts={linkedAccountDetails}
        metaAccounts={linkedMetaAccountDetails.map((a) => ({
          id: a.id,
          businessName: a.businessName,
        }))}
        onSelectGoogle={(accountId) => {
          setShowOmniAccountPicker(false);
          setPreSelectedGoogleAccountId(accountId);
          setShowCreateCampaignWizard(true);
        }}
        onSelectMeta={(accountId) => {
          setShowOmniAccountPicker(false);
          setPreSelectedMetaAccountId(accountId);
          setShowCreateMetaCampaignWizard(true);
        }}
        onCancel={() => setShowOmniAccountPicker(false)}
      />

      {/* Feedback Card - Fallback for smaller screens (xl and above shows in top section) */}
      {feedback.isVisible && feedback.currentQuestion && (
        <div className="xl:hidden fixed bottom-4 right-4 z-40 w-auto max-w-[min(14rem,calc(100vw-2rem))]">
          <FeedbackCard
            question={feedback.currentQuestion}
            onSubmit={feedback.submitResponse}
            isSubmitting={feedback.isSubmitting}
            error={feedback.error}
          />
        </div>
      )}

      {/* Save Changes Bar - appears when there are pending changes */}
      <SaveChangesBar
        hasChanges={accountChanges.hasChanges}
        getChangeCount={accountChanges.getChangeCount}
        getChangeSummary={accountChanges.getChangeSummary}
        onSave={accountChanges.onSave}
        onDiscard={accountChanges.onDiscard}
        isExternallySaving={accountChanges.isExternallySaving}
        onSaveSuccess={async () => {
          toast.success('All changes saved successfully!');
          // Refresh campaign data after successful save - await to ensure data is loaded
          // before SaveChangesBar clears pending changes
          try {
            await refetchCampaigns({ includeCampaignsQuery: true });
            // Also invalidate app-config to refresh any configuration changes
            await queryClient.invalidateQueries({ queryKey: ['app-config'] });
          } catch (error) {
            debug.error('Failed to refresh data after saving changes', error);
            toast.error('Changes were saved, but we could not refresh the latest data. Please try reloading the page.');
          }
        }}
        onSaveError={(errors: string[]) => {
          errors.forEach((error: string) => toast.error(error));
        }}
      />
    </div>
    </ErrorBoundary>
  );
}